import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { AgentDraft, AgentProductPreviewActions, PricingCondition, PricingGroupSetup, PricingRule, PrintingMethodSetup } from './types';
import { AgentProductSidePreview } from './AgentProductSidePreview';

const Preview = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    padding: 20px;
    background: #f5f7fa;
`;

const EmptyState = styled.div`
    display: flex;
    min-height: 220px;
    align-items: center;
    justify-content: center;
    padding: 32px;
    border: 1px dashed #cfd8dc;
    border-radius: 12px;
    color: #607d8b;
    text-align: center;
`;

const ProductHeading = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
`;

const ProductName = styled.h3`
    margin: 0;
    color: #263238;
    font-size: 18px;
`;

const ProductMeta = styled.p`
    margin: 4px 0 0;
    color: #78909c;
    font-size: 12px;
`;

const Revision = styled.span`
    padding: 4px 8px;
    border-radius: 999px;
    background: #e3f2fd;
    color: #1565c0;
    font-size: 11px;
    font-weight: 600;
`;

const Variant = styled.details`
    flex-shrink: 0;
    border: 1px solid #dfe5e8;
    border-radius: 12px;
    background: white;
    overflow: hidden;

    > summary {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 14px;
        cursor: pointer;
        font-weight: 600;
        list-style: none;
    }

    > summary::-webkit-details-marker {
        display: none;
    }
`;

const Swatch = styled.span<{ $color: string }>`
    width: 14px;
    height: 14px;
    border: 1px solid rgba(0, 0, 0, 0.14);
    border-radius: 50%;
    background: ${(props) => props.$color};
`;

const SideCount = styled.span`
    margin-left: auto;
    color: #78909c;
    font-size: 11px;
    font-weight: 400;
`;

const Sides = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
    gap: 12px;
    padding: 0 12px 12px;
`;

const Reference = styled.span`
    color: #90a4ae;
    font-family: monospace;
    font-size: 9px;
    font-weight: 400;
`;

const Status = styled.span<{ $missing?: boolean }>`
    padding: 3px 6px;
    border-radius: 999px;
    background: ${(props) => (props.$missing ? '#fff3e0' : '#e8f5e9')};
    color: ${(props) => (props.$missing ? '#e65100' : '#2e7d32')};
    font-size: 9px;
    font-weight: 600;
`;

const EntityCard = styled.section`
    flex-shrink: 0;
    padding: 16px;
    border: 1px solid #dfe5e8;
    border-radius: 12px;
    background: white;
`;

const SectionHeading = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;

    h4 {
        margin: 0;
        color: #263238;
        font-size: 15px;
    }

    p {
        margin: 3px 0 0;
        color: #78909c;
        font-size: 11px;
    }
`;

const DetailsGrid = styled.dl`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 12px;
    margin: 0;

    div {
        min-width: 0;
        padding: 10px;
        border-radius: 8px;
        background: #f5f7fa;
    }

    dt {
        margin-bottom: 4px;
        color: #78909c;
        font-size: 10px;
        text-transform: uppercase;
    }

    dd {
        overflow: hidden;
        margin: 0;
        color: #37474f;
        font-size: 12px;
        font-weight: 600;
        text-overflow: ellipsis;
    }
`;

const CapabilityList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
`;

const AdvancedSettings = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 14px;
`;

const AdvancedSetting = styled.details`
    border: 1px solid #eceff1;
    border-radius: 8px;
    background: #fafbfc;
    overflow: hidden;

    > summary {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 9px 10px;
        cursor: pointer;
        color: #455a64;
        font-size: 11px;
        font-weight: 600;
        list-style: none;
    }

    > summary::-webkit-details-marker {
        display: none;
    }
`;

const AdvancedSettingMeta = styled.span`
    margin-left: auto;
    color: #90a4ae;
    font-size: 10px;
    font-weight: 400;
`;

const AdvancedSettingBody = styled.div`
    padding: 0 10px 10px;
`;

const AdvancedJson = styled.pre`
    max-height: 220px;
    margin: 0;
    padding: 9px;
    overflow: auto;
    border-radius: 6px;
    background: #263238;
    color: #eceff1;
    font-family: monospace;
    font-size: 10px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

const AssignmentDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: #455a64;
    font-size: 11px;

    > div {
        padding: 7px 8px;
        border-radius: 6px;
        background: white;
    }

    strong {
        font-weight: 600;
    }

    span {
        color: #78909c;
    }
`;

const PricingDescription = styled.p`
    margin: -4px 0 14px;
    color: #607d8b;
    font-size: 12px;
    line-height: 1.5;
`;

const PricingInfo = styled.div`
    margin-top: 14px;
    padding: 10px;
    border-radius: 8px;
    background: #f5f7fa;

    h5 {
        margin: 0 0 4px;
        color: #78909c;
        font-size: 10px;
        text-transform: uppercase;
    }

    p {
        margin: 0;
        color: #455a64;
        font-size: 11px;
        line-height: 1.45;
    }
`;

const Rules = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 14px;
`;

const Rule = styled.details`
    border: 1px solid #eceff1;
    border-radius: 9px;
    background: #fafbfc;
    overflow: hidden;

    > summary {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px;
        cursor: pointer;
        list-style: none;
    }

    > summary::-webkit-details-marker {
        display: none;
    }
`;

const RuleName = styled.span`
    min-width: 0;
    overflow: hidden;
    color: #37474f;
    font-size: 12px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RuleMeta = styled.span`
    margin-left: auto;
    color: #78909c;
    font-size: 10px;
    white-space: nowrap;
`;

const RuleBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 10px 10px;
`;

const ConditionTable = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #eceff1;
    border-radius: 7px;
    background: white;
`;

const ConditionRow = styled.div`
    display: grid;
    grid-template-columns: minmax(105px, 1.4fr) minmax(90px, 1fr) minmax(70px, 0.8fr);
    gap: 8px;
    padding: 8px;
    color: #455a64;
    font-size: 11px;

    &:not(:last-child) {
        border-bottom: 1px solid #eceff1;
    }

    span:last-child {
        text-align: right;
    }
`;

const Subsection = styled.div`
    h5 {
        margin: 0 0 6px;
        color: #78909c;
        font-size: 10px;
        text-transform: uppercase;
    }
`;

const TargetList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const Target = styled.span`
    padding: 5px 7px;
    border: 1px solid #eceff1;
    border-radius: 7px;
    background: white;
    color: #455a64;
    font-size: 10px;
`;

const PrintingMethodPreview: FC<{ method: PrintingMethodSetup }> = ({ method }) => {
    const assignments = method.assignments ?? [];
    const sideAssignments = assignments.reduce((count, assignment) => count + (assignment.sides?.length ?? 0), 0);
    const capabilities = [
        method.allowText && T._('Text', 'Admin'),
        method.allowUploads && T._('Uploads', 'Admin'),
        method.allowGallery && T._('Gallery', 'Admin'),
        method.splitColorFiles && T._('Separated colours', 'Admin'),
        method.layeredPdf && T._('Layered PDF', 'Admin')
    ].filter(Boolean) as string[];

    const advancedSections: Array<{ key: string; label: string; value: unknown }> = [
        { key: 'restrictions', label: T._('Editor restrictions', 'Admin'), value: method.restrictions },
        { key: 'outputSettings', label: T._('Output settings', 'Admin'), value: method.outputSettings },
        { key: 'previewDesignEffect', label: T._('Preview design effect', 'Admin'), value: method.previewDesignEffect },
        { key: 'outputDesignEffect', label: T._('Output design effect', 'Admin'), value: method.outputDesignEffect },
        { key: 'backgroundRemoverSettings', label: T._('Background remover', 'Admin'), value: method.backgroundRemoverSettings },
        { key: 'patternSetting', label: T._('Pattern settings', 'Admin'), value: method.patternSetting ?? method.patternSettings },
        { key: 'paletteColors', label: T._('Palette colours', 'Admin'), value: method.paletteColors },
        { key: 'rgbConversions', label: T._('RGB conversions', 'Admin'), value: method.rgbConversions }
    ].filter((section) => section.value !== undefined);

    const effectTypeLabel = (value: unknown) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
        const effect = value as Record<string, unknown>;
        const rawType = effect.type ?? effect.imageEffectType;
        const numericType = typeof rawType === 'number' ? rawType : Number(rawType);
        if (numericType === 1) return T._('Engraving', 'Admin');
        if (numericType === 2) return T._('Print on Wood', 'Admin');
        if (numericType === 3) return T._('Pixelation', 'Admin');
        return typeof effect.imageEffectTypeName === 'string' ? effect.imageEffectTypeName : null;
    };

    const advancedSummary = (section: { key: string; value: unknown }) => {
        if (section.key === 'fonts' && Array.isArray(section.value)) return `${section.value.length}`;
        if (Array.isArray(section.value)) return `${section.value.length}`;
        if (section.value && typeof section.value === 'object') return T._('Configured', 'Admin');
        return section.value == null ? T._('Not configured', 'Admin') : String(section.value);
    };

    const serializeAdvanced = (value: unknown) => {
        try {
            return JSON.stringify(value, null, 2) ?? String(value);
        } catch {
            return String(value);
        }
    };

    return (
        <EntityCard>
            <SectionHeading>
                <div>
                    <h4>{method.name || T._('Unnamed printing method', 'Admin')}</h4>
                    <p>{method.entityReferenceKey || T._('No reference key yet', 'Admin')}</p>
                </div>
                <Status $missing={!method.printingMethodId}>
                    {method.printingMethodId ? `${T._('Created', 'Admin')} #${method.printingMethodId}` : T._('Draft', 'Admin')}
                </Status>
            </SectionHeading>
            <DetailsGrid>
                <div>
                    <dt>{T._('Resolution', 'Admin')}</dt>
                    <dd>{method.dpi ? `${method.dpi} DPI` : T._('Not set', 'Admin')}</dd>
                </div>
                <div>
                    <dt>{T._('Output', 'Admin')}</dt>
                    <dd>{method.outputType === 'filePerColor' ? T._('File per colour', 'Admin') : T._('File per area', 'Admin')}</dd>
                </div>
                <div>
                    <dt>{T._('Formats', 'Admin')}</dt>
                    <dd>{method.outputFileFormats?.map((format) => format.toUpperCase()).join(', ') || T._('Not set', 'Admin')}</dd>
                </div>
                <div>
                    <dt>{T._('Assignments', 'Admin')}</dt>
                    <dd>
                        {assignments.length} {T._('products', 'Admin')}
                        {sideAssignments > 0 ? ` · ${sideAssignments} ${T._('sides', 'Admin')}` : ''}
                    </dd>
                </div>
                <div>
                    <dt>{T._('Colour limit', 'Admin')}</dt>
                    <dd>{method.maxColors ?? T._('No limit', 'Admin')}</dd>
                </div>
                <div>
                    <dt>{T._('Fonts', 'Admin')}</dt>
                    <dd>
                        {method.fonts?.length ?? 0} · {T._('default', 'Admin')} {method.defaultFontFamilyID ?? T._('none', 'Admin')}
                    </dd>
                </div>
                <div>
                    <dt>{T._('Colour management', 'Admin')}</dt>
                    <dd>{method.pantoneEnabled ? T._('Pantone enabled', 'Admin') : T._('Standard colours', 'Admin')}</dd>
                </div>
                {(method.colorProfileFileName || method.colorProfileFileUrl) && (
                    <div>
                        <dt>{T._('ICC profile', 'Admin')}</dt>
                        <dd title={method.colorProfileFileUrl || undefined}>{method.colorProfileFileName || method.colorProfileFileUrl}</dd>
                    </div>
                )}
                {(method.paletteID != null || method.rgbConversionListID != null) && (
                    <div>
                        <dt>{T._('Palettes', 'Admin')}</dt>
                        <dd>
                            {method.paletteID != null ? `#${method.paletteID}` : T._('none', 'Admin')}
                            {' · '}
                            {method.rgbConversionListID != null ? `RGB #${method.rgbConversionListID}` : T._('no RGB list', 'Admin')}
                        </dd>
                    </div>
                )}
            </DetailsGrid>
            {capabilities.length > 0 && (
                <CapabilityList>
                    {capabilities.map((capability) => (
                        <Status key={capability}>{capability}</Status>
                    ))}
                </CapabilityList>
            )}
            {advancedSections.length > 0 && (
                <AdvancedSettings>
                    {advancedSections.map((section) => {
                        const effect = effectTypeLabel(section.value);
                        return (
                            <AdvancedSetting key={section.key}>
                                <summary>
                                    {section.label}
                                    <AdvancedSettingMeta>{effect || advancedSummary(section)}</AdvancedSettingMeta>
                                </summary>
                                <AdvancedSettingBody>
                                    <AdvancedJson>{serializeAdvanced(section.value)}</AdvancedJson>
                                </AdvancedSettingBody>
                            </AdvancedSetting>
                        );
                    })}
                </AdvancedSettings>
            )}
            {assignments.length > 0 && (
                <AdvancedSettings>
                    <AdvancedSetting>
                        <summary>
                            {T._('Assignment details', 'Admin')}
                            <AdvancedSettingMeta>{assignments.length}</AdvancedSettingMeta>
                        </summary>
                        <AdvancedSettingBody>
                            <AssignmentDetails>
                                {assignments.map((assignment, index) => (
                                    <div key={`${assignment.productId}-${index}`}>
                                        <strong>{assignment.productName || `${T._('Product', 'Admin')} #${assignment.productId}`}</strong>
                                        {assignment.productCode && <span> · {assignment.productCode}</span>}
                                        <br />
                                        <span>
                                            {assignment.sides?.length
                                                ? assignment.sides
                                                      .map((side) =>
                                                          side.sideName || side.colorName
                                                              ? `${side.colorName || `#${side.colorId}`} · ${side.sideName || `#${side.sideId}`}`
                                                              : `color #${side.colorId} · side #${side.sideId}`
                                                      )
                                                      .join(', ')
                                                : T._('All sides', 'Admin')}
                                        </span>
                                    </div>
                                ))}
                            </AssignmentDetails>
                        </AdvancedSettingBody>
                    </AdvancedSetting>
                </AdvancedSettings>
            )}
        </EntityCard>
    );
};

const priceStructureLabel = (value?: string) => {
    switch (value) {
        case 'finalPrice':
            return T._('Final price', 'Admin');
        case 'separatedPrices':
            return T._('Separated prices', 'Admin');
        case 'customizationPrice':
            return T._('Customization price', 'Admin');
        default:
            return value || T._('Not set', 'Admin');
    }
};

const assignmentLabel = (value?: string) => {
    switch (value) {
        case 'products':
            return T._('Products', 'Admin');
        case 'printingMethods':
            return T._('Printing methods', 'Admin');
        default:
            return value || T._('None', 'Admin');
    }
};

const yesNo = (value?: boolean) => {
    if (value == null) return T._('Not set', 'Admin');
    return value ? T._('Yes', 'Admin') : T._('No', 'Admin');
};

const matchModeLabel = (value?: string) => {
    if (value === 'allMatch') return T._('All matches', 'Admin');
    if (value === 'firstMatch') return T._('First match', 'Admin');
    return value || T._('Not set', 'Admin');
};

const priceTypeLabel = (value?: string) => {
    if (value === 'percentage') return T._('Percentage', 'Admin');
    if (value === 'fixed') return T._('Fixed price', 'Admin');
    return value || T._('Not set', 'Admin');
};

const formatNumber = (value?: number) => (value == null ? T._('Not set', 'Admin') : value.toLocaleString());

const conditionFormula = (condition: PricingCondition) => {
    const operator = condition.formula?.operator || '?';
    const values = condition.formula?.values ?? [];
    if (operator === 'between') return `${T._('Between', 'Admin')} ${values.map((value) => formatNumber(value)).join(' – ')}`;
    return `${operator} ${values.map((value) => formatNumber(value)).join(', ')}`;
};

const priceToAdd = (condition: PricingCondition, rule: PricingRule) => {
    const value = formatNumber(condition.priceToAdd);
    return rule.priceType === 'percentage' && condition.priceToAdd != null ? `${value}%` : value;
};

const targetLabel = (target: NonNullable<PricingRule['targets']>[number], index: number) => {
    const parts = [target.color, target.side, target.area].filter((value): value is string => Boolean(value));
    const label = parts.length > 0 ? parts.join(' · ') : `${T._('Target', 'Admin')} ${index + 1}`;
    return target.targetId ? `${label} · #${target.targetId}` : label;
};

const PricingRulePreview: FC<{ rule: PricingRule; index: number }> = ({ rule, index }) => {
    const conditions = [...(rule.conditions ?? [])].sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
    const targets = rule.targets ?? [];
    const title = rule.elementTitle || (rule.propertyId ? `${T._('Property', 'Admin')} #${rule.propertyId}` : `${T._('Rule', 'Admin')} ${index + 1}`);

    return (
        <Rule open={index === 0}>
            <summary>
                <RuleName>{title}</RuleName>
                {rule.ruleId ? <Reference>#{rule.ruleId}</Reference> : null}
                <RuleMeta>
                    {conditions.length} {conditions.length === 1 ? T._('condition', 'Admin') : T._('conditions', 'Admin')}
                </RuleMeta>
            </summary>
            <RuleBody>
                <CapabilityList>
                    <Status>{matchModeLabel(rule.matchMode)}</Status>
                    <Status>{priceTypeLabel(rule.priceType)}</Status>
                    <Status>{rule.isSetupPrice ? T._('Setup price', 'Admin') : T._('Standard price', 'Admin')}</Status>
                    <Status>{rule.simplified ? T._('Simplified', 'Admin') : T._('Advanced', 'Admin')}</Status>
                </CapabilityList>
                {(rule.propertyId || rule.elementId) && (
                    <TargetList>
                        {rule.propertyId ? <Target>{`${T._('Property', 'Admin')} #${rule.propertyId}`}</Target> : null}
                        {rule.elementId ? <Target>{`${T._('Element', 'Admin')} #${rule.elementId}`}</Target> : null}
                    </TargetList>
                )}
                <Subsection>
                    <h5>{T._('Conditions', 'Admin')}</h5>
                    {conditions.length > 0 ? (
                        <ConditionTable>
                            {conditions.map((condition, conditionIndex) => (
                                <ConditionRow key={condition.conditionId || `${condition.position ?? 0}-${conditionIndex}`}>
                                    <span>
                                        {condition.conditionId ? <Reference>#{condition.conditionId} · </Reference> : null}
                                        {conditionFormula(condition)}
                                    </span>
                                    <span>
                                        {T._('Quantity', 'Admin')}: {formatNumber(condition.quantity)}
                                    </span>
                                    <span>
                                        {T._('Price', 'Admin')}: {priceToAdd(condition, rule)}
                                    </span>
                                </ConditionRow>
                            ))}
                        </ConditionTable>
                    ) : (
                        <ProductMeta>{T._('No conditions', 'Admin')}</ProductMeta>
                    )}
                </Subsection>
                {targets.length > 0 && (
                    <Subsection>
                        <h5>{T._('Targets', 'Admin')}</h5>
                        <TargetList>
                            {targets.map((target, targetIndex) => (
                                <Target key={target.targetId || targetIndex}>{targetLabel(target, targetIndex)}</Target>
                            ))}
                        </TargetList>
                    </Subsection>
                )}
            </RuleBody>
        </Rule>
    );
};

const PricingPreview: FC<{ pricing: PricingGroupSetup }> = ({ pricing }) => {
    const rules = pricing.rules ?? [];
    const assignments = pricing.assignments;
    const targetIds = assignments?.targetIds ?? [];

    return (
        <EntityCard>
            <SectionHeading>
                <div>
                    <h4>{pricing.name || T._('Unnamed Advanced Pricing', 'Admin')}</h4>
                    <p>{T._('Advanced Pricing', 'Admin')}</p>
                </div>
                <Status $missing={!pricing.pricingGroupId}>
                    {pricing.pricingGroupId ? `${T._('Created', 'Admin')} #${pricing.pricingGroupId}` : T._('Draft', 'Admin')}
                </Status>
            </SectionHeading>
            {pricing.description && <PricingDescription>{pricing.description}</PricingDescription>}
            <DetailsGrid>
                <div>
                    <dt>{T._('Price structure', 'Admin')}</dt>
                    <dd>{priceStructureLabel(pricing.priceStructure)}</dd>
                </div>
                <div>
                    <dt>{T._('Rules', 'Admin')}</dt>
                    <dd>{rules.length}</dd>
                </div>
                <div>
                    <dt>{T._('Assignments', 'Admin')}</dt>
                    <dd>
                        {assignmentLabel(assignments?.targetType)}
                        {targetIds.length > 0 ? ` · ${targetIds.length}` : ''}
                    </dd>
                </div>
                <div>
                    <dt>{T._('Detailed price', 'Admin')}</dt>
                    <dd>{yesNo(pricing.showDetailedPrice)}</dd>
                </div>
                <div>
                    <dt>{T._('Price description', 'Admin')}</dt>
                    <dd>{yesNo(pricing.priceDescriptionEnabled)}</dd>
                </div>
                <div>
                    <dt>{T._('Quantity as unit', 'Admin')}</dt>
                    <dd>{yesNo(pricing.quantityAsUnit)}</dd>
                </div>
            </DetailsGrid>
            {pricing.infoPricingText && (
                <PricingInfo>
                    <h5>{T._('Pricing information', 'Admin')}</h5>
                    <p>{pricing.infoPricingText}</p>
                </PricingInfo>
            )}
            {targetIds.length > 0 && (
                <Subsection>
                    <h5>{T._('Assigned IDs', 'Admin')}</h5>
                    <TargetList>
                        {targetIds.map((targetId) => (
                            <Target key={targetId}>#{targetId}</Target>
                        ))}
                    </TargetList>
                </Subsection>
            )}
            {rules.length > 0 ? (
                <Rules>
                    {rules.map((rule, index) => (
                        <PricingRulePreview key={rule.ruleId || index} rule={rule} index={index} />
                    ))}
                </Rules>
            ) : (
                <EmptyState>{T._('No pricing rules have been added yet.', 'Admin')}</EmptyState>
            )}
        </EntityCard>
    );
};

interface AgentTaskPreviewProps extends AgentProductPreviewActions {
    draft: AgentDraft;
    disabled?: boolean;
}

export const hasUsefulAgentPreview = (draft: AgentDraft): boolean => {
    const document = draft.document;
    return Boolean(
        document?.product?.variants?.some((variant) => variant.sides?.some((side) => side.image?.url || side.areas?.length)) ||
        document?.printingMethodSetup?.printingMethod?.name?.trim() ||
        document?.pricingSetup?.pricingGroup?.rules?.length
    );
};

export const AgentTaskPreview: FC<AgentTaskPreviewProps> = ({ draft, disabled, ...actions }) => {
    const product = draft.document?.product;
    const printingMethod = draft.document?.printingMethodSetup?.printingMethod;
    const pricing = draft.document?.pricingSetup?.pricingGroup;
    if (!product && !printingMethod && !pricing) {
        return (
            <Preview>
                <EmptyState>{T._('A preview of the current task will appear here when available.', 'Admin')}</EmptyState>
            </Preview>
        );
    }

    const variants = product?.variants ?? [];

    return (
        <Preview>
            {product && (
                <ProductHeading>
                    <div>
                        <ProductName>{product.name || T._('Unnamed product', 'Admin')}</ProductName>
                        <ProductMeta>
                            {variants.length} {variants.length === 1 ? T._('variant', 'Admin') : T._('variants', 'Admin')} · {product.printTypes?.length ?? 0}{' '}
                            {T._('printing methods', 'Admin')}
                        </ProductMeta>
                    </div>
                    {draft.revisionNumber != null && (
                        <Revision>
                            {T._('Revision', 'Admin')} {draft.revisionNumber}
                        </Revision>
                    )}
                </ProductHeading>
            )}

            {product &&
                (variants.length === 0 ? (
                    <EmptyState>{T._('No variants have been added yet.', 'Admin')}</EmptyState>
                ) : (
                    variants.map((variant, index) => {
                        const sides = variant.sides ?? [];
                        return (
                            <Variant key={variant.entityReferenceKey ?? index} open={index === 0}>
                                <summary>
                                    {variant.hexCode && <Swatch $color={variant.hexCode} />}
                                    <span>{variant.name || `${T._('Variant', 'Admin')} ${index + 1}`}</span>
                                    <SideCount>
                                        {sides.length} {sides.length === 1 ? T._('side', 'Admin') : T._('sides', 'Admin')}
                                    </SideCount>
                                </summary>
                                <Sides>
                                    {sides.map((side, sideIndex) => (
                                        <AgentProductSidePreview
                                            key={side.entityReferenceKey ?? sideIndex}
                                            variantRef={variant.entityReferenceKey ?? ''}
                                            side={side}
                                            revisionNumber={draft.drafts?.find((item) => item.kind === 'product')?.revisionNumber ?? draft.revisionNumber}
                                            disabled={disabled}
                                            {...actions}
                                        />
                                    ))}
                                </Sides>
                            </Variant>
                        );
                    })
                ))}

            {printingMethod && <PrintingMethodPreview method={printingMethod} />}
            {pricing && <PricingPreview pricing={pricing} />}
        </Preview>
    );
};
